import { Typography } from "@mui/material"
import { HomePageCarStripDto } from "api"
import NextLink from "common/bit/NextLink"
import { Desktop, Mobile } from "common/MediaQueries"
import PreSaleTag from "common/PreSaleTag"
import { useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"

const StyledTitleDiv = styled.div<{ $isshop?: boolean; $isPreSale?: boolean }>`
  text-align: center;
  flex-shrink: 0;
  margin-top: 20px;
  position: relative;
  ${(p) => p.theme.breakpoints.up("md")} {
    ${(p) =>
      p.$isPreSale
        ? "height: 53px;"
        : !p.$isshop
        ? "height: 117px;"
        : "height: 65px;"}
  }
  @media (min-width: 1281px) {
    ${(p) => (!p.$isshop ? "height: 117px;" : "height: 65px;")}
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 50px;
  }
  ${(p) =>
    p.$isshop &&
    css`
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      ${(p) => p.theme.breakpoints.down("sm")} {
        p {
          font-size: 24px;
        }
      }
    `}
`
const StyledWrapperText = styled.div`
  max-width: calc(100vw - 20px);
  width: 100%;
  overflow: hidden;
`

const StyledTitle = styled(Typography).attrs({
  variant: "body1",
})<{ $fontSize: number }>`
  width: max-content;
  font-size: 32px;
  font-size: ${(p) => p.$fontSize}px;
  margin: 0 auto;
  height: 45px;
  letter-spacing: 0.06em;
  font-weight: 400;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 32px;
  }
`

const StyledPresaleWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
  z-index: 1;
`

const StyledComingSoonText = styled(Typography).attrs({
  variant: "h1",
})`
  color: ${(p) => p.theme.colors.mazdaRed};
  height: 40px;
`

const StyledText = styled(Typography).attrs({
  variant: "body1",
})`
  font-size: 20px;
  height: 32px;
  letter-spacing: 0.08em;
`

type SwiperTopTextProps = {
  carData: HomePageCarStripDto
  carLink: string
  isshop?: boolean
  isPreSale?: boolean
}

interface SwiperTopTextContentProps {
  carData: HomePageCarStripDto
  fontSize: number
  textRef: React.RefObject<HTMLSpanElement>
  $isshop?: boolean
}

const SwiperTopTextContent: React.FC<SwiperTopTextContentProps> = ({
  carData,
  fontSize,
  textRef,
  $isshop: isshop,
}) => (
  <>
    {carData?.carState === "Future" && (
      <StyledComingSoonText>COMING SOON</StyledComingSoonText>
    )}
    <StyledWrapperText>
      <StyledTitle ref={textRef} $fontSize={fontSize}>
        {carData?.name}
      </StyledTitle>
    </StyledWrapperText>
    {!isshop && <StyledText>{carData?.secondaryTitle}</StyledText>}
  </>
)

const SwiperTopText = ({
  carData,
  carLink,
  isshop,
  isPreSale,
}: SwiperTopTextProps) => {
  const [fontSize, setFontSize] = useState(32)
  const textRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const text = textRef.current
    if (!text) return
    const textRect = text.getClientRects()[0]
    const textWidth = textRect.width
    const elementWidth = text.parentElement?.offsetWidth
    if (elementWidth && textWidth >= elementWidth) {
      //text is more than one line
      //adjust the font size for it to fit in one line and not overflow
      const fontSize = (elementWidth / textWidth) * 32
      setFontSize(fontSize)
    } else {
      setFontSize(32)
    }
  }, [carData.name])

  return (
    <StyledTitleDiv
      role="heading"
      aria-level={2}
      $isshop={isshop}
      $isPreSale={isPreSale}
    >
      {isPreSale && (
        <StyledPresaleWrapper>
          <Mobile>
            <PreSaleTag size="small" />
          </Mobile>
          <Desktop>
            <PreSaleTag size="large" />
          </Desktop>
        </StyledPresaleWrapper>
      )}
      {carLink ? (
        <NextLink href={carLink}>
          <SwiperTopTextContent
            carData={carData}
            fontSize={fontSize}
            textRef={textRef}
            $isshop={isshop}
          />
        </NextLink>
      ) : (
        <SwiperTopTextContent
          carData={carData}
          fontSize={fontSize}
          textRef={textRef}
          $isshop={isshop}
        />
      )}
    </StyledTitleDiv>
  )
}

export default SwiperTopText
